import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  Moon,
  Sun,
  Send,
  Linkedin,
  Smartphone,
  Monitor,
  Palette,
  TrendingUp,
  Brain,
  ExternalLink,
  ArrowRight,
  Check,
  ChevronDown,
  Instagram,
  Globe,
} from "lucide-react";

// Asset Imports
import Logo from "./assets/images/white-1.svg";
import img from "./assets/images/datingapp.jpeg";
import img1 from "./assets/images/learnogic.jpeg";
import img2 from "./assets/images/chat.jpg";
import img3 from "./assets/images/artvile.jpeg";
import img4 from "./assets/images/vizentry.png";

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const App = () => {
  const [isDark, setIsDark] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");

  // Handle scroll effects
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      const sections = ["home", "expertise", "projects", "contact"];
      const current = sections.find((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (sectionId === "download") {
      const link = document.createElement("a");
      link.href = "/Nexonetics_brochure.pdf";
      link.download = "Nexonetics_brochure.pdf";
      link.setAttribute("download", "Nexonetics_brochure.pdf");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
    setIsMenuOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      alert("Please fill in all fields");
      return;
    }
    setIsSubmitting(true);
    setTimeout(() => {
      setSubmitStatus(
        "Message sent successfully! We will get back to you soon."
      );
      setFormData({ name: "", email: "", message: "" });
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(""), 5000);
    }, 2000);
  };

  const navItems = [
    { label: "Home", id: "home" },
    { label: "Expertise", id: "expertise" },
    { label: "Projects", id: "projects" },
    { label: "Contact", id: "contact" },
    { label: "Download", id: "download" },
  ];

  const expertiseData = [
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Mobile Development",
      description:
        "Transform your ideas into reality with expert mobile development for iOS and Android platforms.",
      features: ["Native iOS/Android", "Cross-platform", "UI/UX Design"],
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "AI & Machine Learning",
      description:
        "Unlock the power of data with cutting-edge AI and Machine Learning solutions.",
      features: ["Predictive Analytics", "NLP Processing", "Custom AI Models"],
    },
    {
      icon: <Monitor className="w-8 h-8" />,
      title: "Web Development",
      description:
        "Elevate your online presence with dynamic and responsive websites.",
      features: ["Responsive Design", "Full-Stack", "Progressive Web Apps"],
    },
    {
      icon: <Monitor className="w-8 h-8" />,
      title: "Desktop Applications",
      description:
        "Empower your business with robust desktop applications tailored to your needs.",
      features: ["Cross-platform", "High Performance", "Custom Solutions"],
    },
    {
      icon: <Palette className="w-8 h-8" />,
      title: "UI/UX Design",
      description:
        "Create intuitive and engaging interfaces with expert UI/UX development.",
      features: ["User Research", "Prototyping", "Design Systems"],
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Digital Marketing",
      description:
        "Expand your reach and grow your business with strategic digital marketing.",
      features: ["SEO Optimization", "Social Media", "PPC Campaigns"],
    },
  ];

  // Updated Projects Array with Links
  const projects = [
    {
      title: "Learning Platform – The Ultimate Hub",
      description:
        "This is an advanced educational platform designed to provide students with a seamless and engaging learning experience. It offers a dynamic environment for personal and professional growth.",
      image: img1,
      status: "Live",
      tags: ["React", "Python", "PostgreSQL", "Interactive Learning"],
      link: "https://learnogic.com/",
    },
    {
      title: "Dating App – Meaningful Connections",
      description:
        "(UNDER DEVELOPMENT) This modern dating app connects individuals based on compatibility, shared values, and long-term goals. It allows users to create detailed profiles that highlight personal traits, lifestyle preferences, and future aspirations.",
      image: img,
      status: "Under Development",
      tags: ["Flutter", "FastApi", "AI Matching", "Real-time"],
      // No link
    },
    {
      title: "Real-Time Chat Application",
      description:
        "(UNDER DEVELOPMENT) This modern chat app is designed for seamless, real-time messaging with a focus on privacy and security. It features end-to-end encryption, multimedia sharing, and AI-powered smart replies.",
      image: img2,
      status: "Under Development",
      tags: ["WebSocket", "Flutter", "FastApi", "E2E Encryption"],
      // No link
    },
    {
      title: "Vizentra – Advanced Face Recognition Made Simple",
      description:
        "An intelligent face recognition platform that uses Deep Learning to detect and assign persistent Face IDs across multiple uploads. Simply upload any video or image, and Vizentra automatically analyzes each face, assigns consistent Face IDs, and delivers downloadable results.",
      image: img4,
      status: "Live",
      tags: ["React.js", "FastAPI", "Deep Learning", "Image Processing"],
      link: "https://vizentry.com/",
    },
    {
      title: "Artville Academy – Comprehensive Dashboard",
      description:
        "We developed a feature-rich dashboard for Artville Academy, catering to different user roles, including students, teachers, admins, and branch admins. This robust system ensures a seamless learning and administrative experience.",
      image: img3,
      status: "Completed",
      tags: ["React.js", "Django", "MySQL", "Role-Based Access"],
      link: "https://www.artvilleacademy.com/",
    },
  ];

  return (
    <div
      className={`min-h-screen transition-all duration-300 ${
        isDark ? "bg-gray-900 text-white" : "bg-white text-gray-900"
      }`}
    >
      {/* Navigation */}
      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled
            ? isDark
              ? "bg-gray-900/95 backdrop-blur-lg border-b border-gray-800 shadow-xl"
              : "bg-white/95 backdrop-blur-lg border-b border-gray-200 shadow-xl"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0">
              <img className="w-44" src={Logo} alt="Logo" />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-1">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      activeSection === item.id
                        ? "bg-gradient-to-r from-violet-500 to-purple-500 text-white shadow-lg"
                        : isDark
                        ? "text-gray-300 hover:text-white hover:bg-gray-800"
                        : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center space-x-2">
              <button
                onClick={() => setIsDark(!isDark)}
                className={`p-2 rounded-full transition-all duration-200 ${
                  isDark ? "bg-gray-800" : "bg-gray-100"
                }`}
              >
                {isDark ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`p-2 rounded-full transition-all duration-200 ${
                  isDark ? "bg-gray-800" : "bg-gray-100"
                }`}
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className={`md:hidden overflow-hidden ${
                isDark ? "bg-gray-900/95" : "bg-white/95"
              } backdrop-blur-lg border-t ${
                isDark ? "border-gray-800" : "border-gray-200"
              }`}
            >
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`block w-full text-left px-3 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
                      activeSection === item.id
                        ? "bg-gradient-to-r from-violet-500 to-purple-500 text-white"
                        : isDark
                        ? "text-gray-300 hover:text-white hover:bg-gray-800"
                        : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Section */}
      <section
        id="home"
        className="pt-24 lg:pt-32 min-h-screen flex items-center justify-center relative overflow-hidden"
      >
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-br from-violet-500/10 via-purple-500/5 to-pink-500/10"></div>
          <motion.div
            animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
            transition={{
              duration: 10,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-500 rounded-full mix-blend-multiply filter blur-[100px] opacity-20"
          ></motion.div>
          <motion.div
            animate={{ scale: [1, 1.2, 1], rotate: [0, -5, 5, 0] }}
            transition={{
              duration: 15,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-[100px] opacity-20"
          ></motion.div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="space-y-8 text-center lg:text-left"
            >
              <div className="space-y-4">
                <motion.h1
                  variants={fadeInUp}
                  className="text-5xl sm:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-violet-500 via-purple-500 to-pink-500 bg-clip-text text-transparent leading-tight pb-2"
                >
                  Nexonetics
                </motion.h1>
                <motion.p
                  variants={fadeInUp}
                  className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto lg:mx-0 leading-relaxed"
                >
                  Transforming your Vision into Reality with cutting-edge
                  technology and innovative AI solutions.
                </motion.p>
              </div>

              <motion.div
                variants={fadeInUp}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              >
                {/* View Work Button */}
                <button
                  onClick={() => scrollToSection("projects")}
                  className="group px-8 py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold rounded-full hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-violet-500/25 flex items-center justify-center space-x-2"
                >
                  <span>View Our Work</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                </button>

                {/* Visit Official Site Button */}
                <a
                  href="https://nexonetics.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`px-8 py-4 font-semibold rounded-full border-2 border-violet-600 flex items-center justify-center space-x-2 transition-all duration-300 ${
                    isDark
                      ? "text-violet-400 hover:bg-violet-600 hover:text-white"
                      : "text-violet-600 hover:bg-violet-600 hover:text-white"
                  }`}
                >
                  <Globe className="w-5 h-5" />
                  <span>Visit Official Site</span>
                </a>
              </motion.div>
            </motion.div>

            {/* Hero Image Section */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 aspect-square lg:aspect-[4/3]">
                <img
                  src="https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=2070&auto=format&fit=crop"
                  alt="Future Technology"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-violet-600/20 to-transparent mix-blend-overlay"></div>

                {/* Floating Badge Animation */}
                <motion.div
                  animate={{ y: [-10, 10, -10] }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute bottom-8 left-8 bg-white/10 backdrop-blur-md border border-white/20 p-4 rounded-xl shadow-xl max-w-xs"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-violet-500 to-pink-500 flex items-center justify-center">
                      <Brain className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">
                        AI Powered
                      </p>
                      <p className="text-white/70 text-xs">
                        Next Gen Solutions
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 cursor-pointer"
          onClick={() => scrollToSection("expertise")}
        >
          <ChevronDown
            className={`w-8 h-8 ${isDark ? "text-gray-400" : "text-gray-600"}`}
          />
        </motion.div>
      </section>

      {/* Expertise Section */}
      <section
        id="expertise"
        className={`py-20 ${isDark ? "bg-gray-800/50" : "bg-gray-50"}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-violet-500 to-purple-500 bg-clip-text text-transparent">
              Our Expertise
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              We provide comprehensive digital solutions to transform your
              business and bring your vision to life.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          >
            {expertiseData.map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -10 }}
                className={`group p-6 lg:p-8 rounded-2xl transition-all duration-300 ${
                  isDark
                    ? "bg-gray-900/80 hover:bg-gray-800 border border-gray-700 hover:border-violet-500/50"
                    : "bg-white hover:bg-gray-50 border border-gray-200 hover:border-violet-500/50"
                } shadow-lg hover:shadow-2xl`}
              >
                <div className="text-violet-500 mb-6 group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 group-hover:text-violet-500 transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                  {item.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {item.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className={`px-3 py-1 text-sm rounded-full ${
                        isDark
                          ? "bg-gray-800 text-gray-300"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-violet-500 to-purple-500 bg-clip-text text-transparent">
              Our Projects
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Discover some of our latest work and see how we've helped
              businesses achieve their goals.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`group rounded-2xl overflow-hidden transition-all duration-300 flex flex-col ${
                  isDark
                    ? "bg-gray-800/80 border border-gray-700 hover:border-violet-500/50"
                    : "bg-white border border-gray-200 hover:border-violet-500/50"
                } shadow-lg hover:shadow-2xl`}
              >
                <div className="aspect-video overflow-hidden relative">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div
                    className={`absolute top-4 right-4 px-3 py-1 rounded-full text-sm font-medium backdrop-blur-sm ${
                      project.status === "Live"
                        ? "bg-green-500/90 text-white"
                        : project.status === "Completed"
                        ? "bg-blue-500/90 text-white"
                        : "bg-yellow-500/90 text-black"
                    }`}
                  >
                    {project.status}
                  </div>
                </div>

                <div className="p-6 lg:p-8 flex flex-col flex-grow">
                  <h3 className="text-xl font-bold mb-3 group-hover:text-violet-500 transition-colors duration-300">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4 flex-grow">
                    {project.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 text-sm rounded-full bg-violet-500/10 text-violet-500 border border-violet-500/20"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Render Link Button ONLY if link exists */}
                  {project.link && (
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 text-violet-500 hover:text-violet-400 font-medium transition-colors group/link"
                      >
                        <span>Visit Live Site</span>
                        <ExternalLink className="w-4 h-4 transform group-hover/link:translate-x-1 group-hover/link:-translate-y-1 transition-transform duration-200" />
                      </a>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className={`py-20 ${isDark ? "bg-gray-800/50" : "bg-gray-50"}`}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-violet-500 to-purple-500 bg-clip-text text-transparent">
              Contact Us
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300">
              Got a project waiting to be realized? Let's collaborate and make
              it happen!
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className={`rounded-2xl p-6 lg:p-12 ${
              isDark
                ? "bg-gray-900/80 border border-gray-700"
                : "bg-white border border-gray-200"
            } shadow-2xl backdrop-blur-sm`}
          >
            {submitStatus && (
              <div
                className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${
                  submitStatus.includes("success")
                    ? "bg-green-100 border border-green-300 text-green-700"
                    : "bg-red-100 border border-red-300 text-red-700"
                }`}
              >
                <Check className="w-5 h-5" />
                <span>{submitStatus}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium mb-2"
                  >
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="What's your name?"
                    required
                    className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-200 ${
                      isDark
                        ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium mb-2"
                  >
                    Email / Phone *
                  </label>
                  <input
                    type="text"
                    id="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    placeholder="How can I reach you?"
                    required
                    className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-200 ${
                      isDark
                        ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium mb-2"
                >
                  Message *
                </label>
                <textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  placeholder="Send me any inquiries or questions"
                  required
                  rows={6}
                  className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all duration-200 resize-none ${
                    isDark
                      ? "bg-gray-800 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group px-8 py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold rounded-full hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send
                    className={`w-5 h-5 transition-transform duration-200 ${
                      isSubmitting
                        ? "animate-pulse"
                        : "group-hover:translate-x-1"
                    }`}
                  />
                  <span>{isSubmitting ? "Sending..." : "Send Message"}</span>
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className={`py-12 ${
          isDark
            ? "bg-gray-900 border-t border-gray-800"
            : "bg-white border-t border-gray-200"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0 text-center md:text-left">
              <h3 className="text-2xl font-bold bg-gradient-to-r from-violet-500 to-purple-500 bg-clip-text text-transparent">
                Nexonetics
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mt-2">
                Transforming your Vision into Reality
              </p>
            </div>
            <div className="flex items-center space-x-6">
              <a
                href="http://linkedin.com/company/nexonetics-solutions-and-services/posts/?feedView=all"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 dark:text-gray-300 hover:text-violet-500 transition-colors duration-200 hover:scale-110 transform"
              >
                <Linkedin className="w-6 h-6" />
              </a>
              <a
                href="https://www.instagram.com/nexonetics/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 dark:text-gray-300 hover:text-violet-500 transition-colors duration-200 hover:scale-110 transform"
              >
                <Instagram className="w-6 h-6" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
